import type { PropsWithChildren } from "react";

import { useHover } from "@react-aria/interactions";

import { colorsPalettes, elevation } from "@similarweb/styles";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { Arrow } from "./Arrow";
import { Content } from "./Content";
import { Favicon } from "./Favicon";
import { Footer } from "./Footer";
import { Header } from "./Header";
import { Title } from "./Title";

type CardLayoutVariant = "simplified" | "full";

export interface ICardLayoutProps extends PropsWithChildren {
    iconUrl: string;
    onClick?: () => void;
    variant?: CardLayoutVariant;
}

export const CardLayout = ({ children, iconUrl, onClick, variant = "full", ...props }: ICardLayoutProps) => {
    const { hoverProps, isHovered } = useHover({});

    return (
        <Flex
            padding="24px 32px 24px 16px"
            gap="16px"
            background={colorsPalettes.carbon[0]}
            cursor="pointer"
            transition="background 0.2s ease-in-out"
            overflow="hidden"
            whenHover={{
                background: colorsPalettes.carbon[25],
            }}
            {...hoverProps}
            data-card-hovered={isHovered}
            tabIndex={0}
            {...(onClick ? { role: "button", onClick } : {})}
            {...(variant === "full" ? { borderRadius: "4px", boxShadow: elevation.Z1 } : {})}
            {...props}
        >
            <Favicon iconUrl={iconUrl} />
            <Stack
                gap="16px"
                width="calc(100% - 32px - 16px)"
            >
                {children}
            </Stack>
        </Flex>
    );
};

CardLayout.Header = Header;
CardLayout.Title = Title;
CardLayout.Arrow = Arrow;
CardLayout.Content = Content;
CardLayout.Footer = Footer;
